/**
 * Crosshair sync across tiles.
 *
 * A hover on one chart highlights the same timestamp on every other open
 * chart in the workspace, including the ones in pop-out windows. Done by
 * posting the hovered epoch-second to a BroadcastChannel; every subscriber
 * exposes it as React state. A move handled this way echoes only through
 * the channel, never back through the chart that produced it, which the
 * Chart component already guards against with `syncCrosshairTime` /
 * `onCrosshair`.
 *
 * The channel is intentionally one per workspace, so toggling sync off in
 * one window stops publishes everywhere -- it is a shared workspace
 * preference, not a per-tile toggle. See useWorkspace().crosshairSync.
 */
import { useEffect, useRef, useState } from "react";

const CHANNEL = "tt-crosshair";

interface CrosshairMsg {
  /** Who published it, so a subscriber can ignore its own messages. */
  from: string;
  /** The hovered bar's epoch seconds, or null when the cursor leaves. */
  time: number | null;
}

/** Random id for this window so a subscriber ignores its own echo. */
export function useWindowId(): string {
  const id = useRef<string>();
  if (!id.current) id.current = Math.random().toString(36).slice(2, 10);
  return id.current;
}

/** Subscribe to crosshair-sync messages. Returns the latest time, or null. */
export function useSyncedCrosshair(windowId: string, enabled: boolean): number | null {
  const [time, setTime] = useState<number | null>(null);
  useEffect(() => {
    if (!enabled) { setTime(null); return; }
    const ch = new BroadcastChannel(CHANNEL);
    const handler = (ev: MessageEvent<CrosshairMsg>) => {
      if (ev.data.from === windowId) return;
      setTime(ev.data.time);
    };
    ch.addEventListener("message", handler);
    return () => {
      ch.removeEventListener("message", handler);
      ch.close();
    };
  }, [windowId, enabled]);
  return time;
}

/** Returns a publisher. Calling it posts the time to every other tile. */
export function useCrosshairPublisher(
  windowId: string, enabled: boolean,
): (time: number | null) => void {
  const chRef = useRef<BroadcastChannel | null>(null);
  useEffect(() => {
    if (!enabled) { chRef.current = null; return; }
    const ch = new BroadcastChannel(CHANNEL);
    chRef.current = ch;
    return () => { ch.close(); chRef.current = null; };
  }, [enabled]);
  return (time: number | null) => {
    if (!enabled) return;
    chRef.current?.postMessage({ from: windowId, time } satisfies CrosshairMsg);
  };
}
